import "@fontsource-variable/montserrat";
import "./styles.css";

const app = requiredElement<HTMLDivElement>("#app");
const resolveEndpoint = import.meta.env.VITE_RESOLVE_SHARE_ENDPOINT || "/api/resolveShare";
const reportEndpoint = import.meta.env.VITE_REPORT_SHARE_ENDPOINT || "/api/reportShare";
const deletionEndpoint = import.meta.env.VITE_DELETION_REQUEST_ENDPOINT || "/api/deletionRequest";

type MediaResolution = {
  title: string;
  mediaType: "video/mp4" | "image/jpeg";
  byteLength: number;
  shareExpiresAt: number | null;
  expiryPolicy: "fixed" | "subscription";
  mediaExpiresAt: number;
  mediaUrl: string;
  downloadUrl: string;
};

const FRAME_SECONDS = 1 / 30;
let controlledVideo: HTMLVideoElement | null = null;

const pages: Record<string, () => string> = {
  "/": landingPage,
  "/brand": brandPage,
  "/privacy": privacyPage,
  "/terms": termsPage,
  "/support": supportPage,
  "/deletion": deletionPage,
  "/abuse": abusePage,
};

void route();

async function route(): Promise<void> {
  const path = normalizedPath(window.location.pathname);
  const shareMatch = /^\/s\/([A-Za-z0-9._-]{1,128})$/.exec(path);
  if (shareMatch?.[1]) {
    document.title = "Shared review · Good Eye Coach";
    app.innerHTML = shell(shareViewerPage(shareMatch[1]), false);
    await initializeShareViewer(shareMatch[1]);
    return;
  }

  const render = pages[path];
  if (!render) {
    document.title = "Page not found · Good Eye Coach";
    app.innerHTML = shell(notFoundPage());
    return;
  }
  document.title = path === "/" ? "See the rep. Shape the next one. · Good Eye" : `${headingFor(path)} · Good Eye Coach`;
  app.innerHTML = shell(render());
  if (path === "/deletion") initializeDeletionForm();
  if (path === "/abuse") initializeReportForm();
}

function shell(content: string, showNav = true): string {
  return `
    <header class="site-header">
      <a class="brand" href="/" aria-label="Good Eye home">
        <span class="brand-mark" aria-hidden="true"><img src="/brand/good-eye-app-icon.png" alt="" /></span>
        <span class="wordmark">Good Eye</span>
      </a>
      ${
        showNav
          ? `<nav aria-label="Main navigation">
              <a href="/brand">Brand</a>
              <a href="/support">Support</a>
            </nav>`
          : `<a class="quiet-link" href="/support">Need help?</a>`
      }
    </header>
    <main id="main">${content}</main>
    <footer>
      <p>© ${new Date().getFullYear()} Good Eye Coach</p>
      <nav aria-label="Legal navigation">
        <a href="/brand">Brand guidelines</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a>
        <a href="/deletion">Delete cloud data</a><a href="/abuse">Report content</a>
      </nav>
    </footer>`;
}

function landingPage(): string {
  return `
    <section class="hero" aria-labelledby="hero-title">
      <div class="hero-copy">
        <div class="eyebrow">Baseball coaching, in focus</div>
        <h1 id="hero-title"><span>See the rep.</span><span>Shape the next one.</span></h1>
        <p class="lede">Capture fast movement, study every frame, and show the player exactly what changes the next repetition.</p>
        <div class="hero-actions">
          <a class="button button-primary" href="#workflow">See the workflow</a>
          <a class="button button-secondary" href="/privacy">How privacy works</a>
        </div>
        <div class="proof-row" aria-label="Product qualities">
          <span>High frame rate</span>
          <span>Frame accurate</span>
          <span>Private by default</span>
        </div>
      </div>
      <div class="motion-card" role="img" aria-label="A Good Eye frame review with a gold coaching arrow tracing a baseball movement">
        <img class="hero-mark" src="/brand/good-eye-mark-brand.png" alt="" />
        <div class="field-lines" aria-hidden="true"></div>
        <span class="frame-chip" aria-hidden="true">FRAME 124</span>
        <svg viewBox="0 0 420 220" aria-hidden="true" focusable="false">
          <path d="M82 168 C145 145, 205 112, 306 62" />
          <path d="M273 59 L309 61 L293 94" />
          <circle cx="82" cy="168" r="11" />
        </svg>
      </div>
    </section>
    <section id="workflow" class="workflow-section" aria-labelledby="features-title">
      <div class="section-heading">
        <span class="eyebrow">From motion to meaning</span>
        <h2 id="features-title" class="section-title">Make the adjustment clear.</h2>
        <p>Good Eye stays focused on the coaching conversation: capture the rep, find the detail, compare the movement, and explain what comes next.</p>
      </div>
      <div class="feature-grid">
        <article><span class="step">01</span><h3>Capture</h3><p>Record fast baseball movement at the best supported frame rate without losing the next rep.</p></article>
        <article><span class="step">02</span><h3>See</h3><p>Move frame by frame and add precise lines, angles, shapes, and timing cues.</p></article>
        <article><span class="step">03</span><h3>Compare</h3><p>Align two reps around the same baseball event and study the difference side by side.</p></article>
        <article><span class="step">04</span><h3>Explain</h3><p>Send a private browser review that preserves slow-motion playback instead of relying on text-message video quality.</p></article>
      </div>
    </section>
    <section class="plus-callout" aria-labelledby="plus-title">
      <div class="plus-copy">
        <span class="eyebrow">Good Eye+</span>
        <h2 id="plus-title">More storage. Better sharing. Access anywhere.</h2>
        <p>Keep finished coaching videos available in your hosted library and share clearer reviews without putting the app’s coaching tools behind a paywall.</p>
        <p class="plus-price"><strong>$4.99</strong><span>/month</span></p>
      </div>
      <ul aria-label="Good Eye+ benefits">
        <li><strong>10 GB</strong><span>cloud storage</span></li>
        <li><strong>1080p</strong><span>shared links</span></li>
        <li><strong>Sync</strong><span>across devices</span></li>
        <li><strong>Restore</strong><span>your library</span></li>
      </ul>
    </section>
    <section class="privacy-callout">
      <div><span class="eyebrow">Private by default</span><h2>Keep the coach in the picture.</h2></div>
      <p>Your projects are not a public feed. Originals and editable projects stay on your device, and recipients receive only the finished export you deliberately host or share.</p>
    </section>`;
}

function brandPage(): string {
  return `
    <article class="brand-guide">
      <header class="brand-guide-hero">
        <div class="brand-guide-intro">
          <span class="eyebrow">Public brand guidelines</span>
          <h1>Baseball coaching, in focus.</h1>
          <p class="lede">Good Eye is precise, human, direct, encouraging, and private. The identity turns keen observation and controlled athletic motion into one unmistakable eye.</p>
          <div class="brand-guide-actions">
            <a class="button" href="/brand/good-eye-mark-brand.png" download>Download primary mark</a>
            <a class="button button-outline" href="/brand/good-eye-app-icon.png" download>Download app icon</a>
          </div>
        </div>
        <figure class="brand-guide-mark">
          <img src="/brand/good-eye-mark-brand.png" alt="The Good Eye luminous focus-aperture eye mark in ballpark navy and seam red" width="1200" height="800" />
          <figcaption>Focus Aperture · approved luminous mark</figcaption>
        </figure>
      </header>

      <nav class="brand-guide-nav" aria-label="Brand guideline sections">
        <a href="#foundation">Foundation</a>
        <a href="#logo">Logo</a>
        <a href="#color">Color</a>
        <a href="#typography">Typography</a>
        <a href="#voice">Voice</a>
      </nav>

      <section id="foundation" class="brand-guide-section">
        <div class="brand-section-heading">
          <span class="eyebrow">01 / Foundation</span>
          <h2>Reveal the detail. Explain the change. Get back to the next rep.</h2>
          <p>Good Eye supports a coach’s judgment; it never presents technology as a replacement for coaching.</p>
        </div>
        <dl class="brand-facts">
          <div><dt>Primary name</dt><dd>Good Eye</dd></div>
          <div><dt>Store + legal title</dt><dd>Good Eye Coach</dd></div>
          <div><dt>Positioning</dt><dd>Baseball coaching, in focus.</dd></div>
          <div><dt>Audience</dt><dd>Adult coaches and performance-focused players</dd></div>
        </dl>
        <div class="brand-principles">
          <article><span>01</span><h3>Keen observation</h3><p>Good coaching starts with noticing the detail that changes the next repetition.</p></article>
          <article><span>02</span><h3>Controlled motion</h3><p>The mark feels athletic and measured—never frantic, clinical, or aggressive.</p></article>
          <article><span>03</span><h3>Human judgment</h3><p>The product reveals the rep. The coach owns the observation and the conversation.</p></article>
        </div>
      </section>

      <section id="logo" class="brand-guide-section brand-guide-section-dark">
        <div class="brand-section-heading">
          <span class="eyebrow">02 / Logo</span>
          <h2>One eye. Two fields. A hidden baseball cue.</h2>
          <p>The two interlocking tapered forms create an eye, aperture, and sense of motion. Three restrained cuts in the navy field echo baseball stitching without turning the mark into a team badge.</p>
        </div>
        <div class="logo-use-grid">
          <figure class="logo-use-card logo-use-card-dark"><img src="/brand/good-eye-mark-brand.png" alt="Good Eye mark on Scoreboard" /><figcaption>Preferred digital treatment</figcaption></figure>
          <figure class="logo-use-card logo-use-card-light"><img src="/brand/good-eye-app-icon.png" alt="Good Eye app icon on Chalk" /><figcaption>Approved square application</figcaption></figure>
        </div>
        <div class="logo-rules">
          <div><h3>Do</h3><ul><li>Use the approved artwork without redrawing it.</li><li>Keep clear space equal to at least one quarter of the mark width.</li><li>Preserve the navy-over-red relationship, open center, and stitch cuts.</li><li>Use the padded square artwork for launcher and app-icon surfaces.</li></ul></div>
          <div><h3>Do not</h3><ul><li>Add a pupil, baseball, bat, mascot, shield, or team crest.</li><li>Stretch, rotate, crop, outline, or rearrange the forms.</li><li>Blend the navy and red fields through purple.</li><li>Place the mark inside an MLB-style split rectangle.</li></ul></div>
        </div>
      </section>

      <section id="color" class="brand-guide-section">
        <div class="brand-section-heading">
          <span class="eyebrow">03 / Color</span>
          <h2>Ballpark heritage. Analytical clarity.</h2>
          <p>Navy, seam red, and chalk make the baseball connection immediate while Good Eye’s composition remains its own.</p>
        </div>
        <div class="brand-swatches" aria-label="Good Eye color palette">
          <div class="brand-swatch swatch-navy"><span>Primary</span><strong>Ballpark Navy</strong><code>#002D72</code></div>
          <div class="brand-swatch swatch-red"><span>Action</span><strong>Seam Red</strong><code>#D50032</code></div>
          <div class="brand-swatch swatch-scoreboard"><span>Foundation</span><strong>Scoreboard</strong><code>#071A33</code></div>
          <div class="brand-swatch swatch-chalk"><span>Canvas</span><strong>Chalk</strong><code>#F7F5F0</code></div>
          <div class="brand-swatch swatch-white"><span>Contrast</span><strong>Ball White</strong><code>#FFFFFF</code></div>
          <div class="brand-swatch swatch-gold"><span>Annotation</span><strong>Foul Pole Gold</strong><code>#F4B942</code></div>
        </div>
        <div class="color-guidance">
          <p><b>60%</b> Ballpark Navy or Scoreboard</p><p><b>30%</b> Chalk or Ball White</p><p><b>10%</b> Seam Red</p>
        </div>
        <p class="brand-note">Use Foul Pole Gold only for annotations, warnings, measurement overlays, or small highlights. Never set red text on navy or navy text on red; use Ball White for copy on either core color.</p>
      </section>

      <section id="typography" class="brand-guide-section brand-type-section">
        <div class="brand-section-heading">
          <span class="eyebrow">04 / Typography</span>
          <h2>Montserrat everywhere.</h2>
          <p>Montserrat is the only primary typeface across the product, website, presentations, social media, print, email, video, and marketplace creative.</p>
        </div>
        <div class="brand-type-specimen" aria-label="Montserrat type specimen">
          <div class="brand-type-display">See what<br />others miss.</div>
          <div class="brand-weight-list">
            <p class="weight-400"><span>400</span> Regular · body copy and captions</p>
            <p class="weight-500"><span>500</span> Medium · supporting copy and metadata</p>
            <p class="weight-600"><span>600</span> SemiBold · buttons, navigation, and labels</p>
            <p class="weight-700"><span>700</span> Bold · subheads, cards, and wordmark</p>
            <p class="weight-800"><span>800</span> ExtraBold · headlines and display</p>
            <p class="weight-900"><span>900</span> Black · very short statements only</p>
          </div>
        </div>
        <div class="type-rules">
          <p><b>Display</b><span>800–900 · 0.9–1.0 line height · tight tracking</span></p>
          <p><b>Sections</b><span>700–800 · 1.0–1.15 line height</span></p>
          <p><b>Body</b><span>400 · 1.5–1.7 line height · normal tracking</span></p>
          <p><b>Controls</b><span>600 · 1.2–1.4 line height</span></p>
        </div>
        <p class="brand-note">Self-host the approved Montserrat files for digital products. Do not substitute Montserrat Alternates, Montserrat Subrayada, or another sans-serif as a deliberate brand choice.</p>
      </section>

      <section id="voice" class="brand-guide-section brand-voice-section">
        <div class="brand-section-heading">
          <span class="eyebrow">05 / Voice</span>
          <h2>Specific enough to help. Human enough to trust.</h2>
        </div>
        <div class="voice-qualities" aria-label="Brand voice qualities"><span>Precise</span><span>Human</span><span>Direct</span><span>Encouraging</span><span>Private</span></div>
        <div class="voice-grid">
          <div><h3>Sound like this</h3><p>“Move frame by frame and show the player exactly what changes the next rep.”</p><p>Use clear coaching verbs: capture, see, compare, explain, and improve.</p></div>
          <div><h3>Avoid this</h3><p>Hype, intimidation, scouting clichés, medical certainty, or language that diminishes the coach-player relationship.</p><p>Measurements are on-screen coaching aids—not diagnoses or guarantees.</p></div>
        </div>
      </section>

      <aside class="brand-legal-note">
        <strong>Brand distinction</strong>
        <p>Good Eye uses the familiar navy, red, and white language of professional baseball. It is independent and is not affiliated with, approved by, or endorsed by Major League Baseball. Do not use MLB’s batter silhouette, split rectangle, proportions, or trade dress.</p>
      </aside>
    </article>`;
}

function privacyPage(): string {
  return articlePage(
    "Privacy policy",
    "Effective September 1, 2026",
    `
      <h2>Our approach</h2>
      <p>Good Eye Coach is designed for adult coaches and parents. It is not directed to children and does not provide child accounts, public profiles, or a public content feed.</p>
      <h2>Information we process</h2>
      <p>Local projects, source clips, EditMasters, annotations, and player history stay on your device. When you explicitly create a hosted link, we process an anonymous or linked account identifier, the finished MP4 or JPEG you chose to host, link state, storage usage, subscription entitlement when applicable, and necessary security logs.</p>
      <p>Camera capture remains silent. If you explicitly start a voice-over, Good Eye requests microphone access at that moment and records a separate narration take. Narration stays on your device unless you include it in a finished export that you then choose to share or host.</p>
      <p>Optional diagnostics may include device and operating-system version, capture profile, failure category, and operation duration. Diagnostics must not include video content, drawing coordinates, bearer secrets, signed URLs, filenames, or raw storage paths.</p>
      <h2>Why we process it</h2>
      <p>We use data to authenticate hosted-share ownership, deliver the finished exports you request, verify optional subscriptions, enforce quotas, prevent abuse, resolve support issues, and meet legal obligations. We do not sell personal information or use shared footage for advertising.</p>
      <h2>Sharing and service providers</h2>
      <p>Private links are bearer links: anyone who receives one can view its export until it expires, the sender's qualifying subscription lapses, or it is revoked. Free links expire exactly three days after creation. Subscription links remain available only while verified billing is active or in grace and do not revive after a lapse. Revocation blocks new media resolutions immediately; a media URL already issued can work for up to five minutes, and a downloaded copy cannot be revoked.</p>
      <p>Cloud hosting, authentication, storage, security checks, and crash reporting are provided through Google Firebase and related Google Cloud services. Apple or Google processes sign-in and purchase information when you choose that provider, and the applicable app store processes subscription billing.</p>
      <h2>Retention and deletion</h2>
      <p>Free hosted media is temporary and becomes eligible for deletion after its three-day link expires. Subscriber-hosted finished media is retained while the subscription is active; after lapse it remains owner-downloadable for 90 days while its old links remain unavailable, then becomes eligible for deletion. Expired link records and security logs may be retained for a limited period for abuse prevention and audit needs. A verified deletion request disables access immediately and is targeted for completion within seven days, except information we must retain by law.</p>
      <h2>Your choices</h2>
      <p>You can use the full app locally without an account, opt out of diagnostics, choose whether to create a hosted link or use cellular data, revoke links, delete anonymous temporary cloud data, and request linked-account deletion in the app or on our <a href="/deletion">deletion page</a>.</p>
      <h2>Contact</h2>
      <p>Privacy questions: <a href="mailto:privacy@goodeye.coach">privacy@goodeye.coach</a>. Security reports: <a href="mailto:security@goodeye.coach">security@goodeye.coach</a>.</p>`,
  );
}

function termsPage(): string {
  return articlePage(
    "Terms of service",
    "Effective August 26, 2026",
    `
      <h2>Who may use Good Eye Coach</h2>
      <p>You must be at least 18 and legally able to accept these terms. The service is intended for adult coaches and parents and is not directed to children.</p>
      <h2>Your content and permissions</h2>
      <p>You retain ownership of content you create. You grant us the limited rights needed to store, process, transmit, and delete content according to your instructions. You promise that you have permission to record every person depicted and to share the content with each intended recipient, including any consent required for minors.</p>
      <h2>Acceptable use</h2>
      <p>Do not use the service for unlawful surveillance, harassment, exploitation, infringement, malware delivery, or content that creates a risk of harm. Do not probe or circumvent access controls, quotas, rate limits, or private links. We may suspend accounts or links to protect people and the service.</p>
      <h2>Private links</h2>
      <p>Keep bearer links confidential. A recipient can download or redistribute an export. Free links last three days; subscription links stop permanently when the qualifying subscription lapses. Revocation cannot erase copies that recipients have already downloaded.</p>
      <h2>Good Eye+ subscriptions</h2>
      <p>The app and all capture, editing, analysis, local export, and native sharing tools are free. An optional Good Eye+ subscription provides higher-quality hosted delivery and persistent storage of explicitly hosted finished media. Billing, renewal, cancellation, and refunds are handled under the applicable app store's terms. A billing grace period counts as active; after actual lapse, hosted-media recovery and link behavior follow the disclosures shown at purchase.</p>
      <h2>Beta service</h2>
      <p>Pre-release features may change, fail, or be withdrawn. Keep your own copy of important content. To the extent permitted by law, the service is provided without warranties and liability is limited to the amount you paid for the service during the prior 12 months.</p>
      <h2>Termination and disputes</h2>
      <p>You may stop using the service and delete your account. We may restrict use for violations, security risk, or legal requirements. Governing-law and dispute-resolution language will be finalized for the operating company’s formation jurisdiction before public release.</p>
      <h2>Contact</h2>
      <p>Questions about these terms: <a href="mailto:support@goodeye.coach">support@goodeye.coach</a>.</p>`,
  );
}

function supportPage(): string {
  return articlePage(
    "Support",
    "We’re here to help with capture, export, access, and privacy questions.",
    `<div class="contact-grid">
      <a class="contact-card" href="mailto:support@goodeye.coach"><span>Product help</span><b>support@goodeye.coach</b><small>Include your app version and device model. Never send a private link secret unless support explicitly requests it.</small></a>
      <a class="contact-card" href="mailto:privacy@goodeye.coach"><span>Privacy</span><b>privacy@goodeye.coach</b><small>Ask about your information, retention, consent, or deletion.</small></a>
      <a class="contact-card" href="mailto:security@goodeye.coach"><span>Security</span><b>security@goodeye.coach</b><small>Privately report a vulnerability. Do not include another person’s media.</small></a>
      <a class="contact-card" href="/abuse"><span>Content concern</span><b>Report shared content</b><small>Send a privacy, safety, copyright, or other content report.</small></a>
    </div>
    <h2>What to include</h2><p>Tell us what you were doing, what happened, your device and app version, and any error message. Do not email video of a minor unless our support team confirms a safe transfer method.</p>`,
  );
}

function deletionPage(): string {
  return articlePage(
    "Delete cloud data",
    "Delete anonymous hosted data in the app, or verify deletion of a linked account.",
    `<h2>What deletion does</h2>
      <p>We immediately block new cloud access, revoke hosted links, and queue deletion of hosted finished exports, account details, and active links. Local projects on your device are unaffected. Active cloud-data deletion is targeted for completion within seven days. Copies downloaded by recipients cannot be deleted by Good Eye Coach.</p>
      <h2>Delete in the app</h2>
      <p>For anonymous sharing data, open <strong>Settings → Good Eye+ → Delete temporary cloud data</strong> and confirm the secure in-app challenge. No provider sign-in is required. For an Apple- or Google-linked account, choose <strong>Delete cloud account</strong>, sign in again, and confirm.</p>
      <h2>Request linked-account deletion from the web</h2>
      <p>Enter the email associated with Apple or Google sign-in. We will require identity verification before processing the request; submitting this form alone does not delete an account. Anonymous data has no email and must be deleted from the installation that owns it.</p>
      <form id="deletion-form" class="stacked-form">
        <label for="deletion-email">Account email</label>
        <input id="deletion-email" name="email" type="email" autocomplete="email" maxlength="254" required />
        <label class="check-row"><input name="understood" type="checkbox" required /><span>I understand that deletion is permanent after processing.</span></label>
        <button type="submit">Begin deletion request</button>
        <p id="deletion-status" class="form-status" role="status" aria-live="polite"></p>
      </form>`,
  );
}

function abusePage(): string {
  const shareId = new URLSearchParams(window.location.search).get("share") ?? "";
  return articlePage(
    "Report shared content",
    "Reports are reviewed for privacy, safety, copyright, and acceptable-use concerns.",
    `<p>If someone is in immediate danger, contact local emergency services. This form is not monitored as an emergency channel.</p>
      <form id="report-form" class="stacked-form">
        <label for="share-id">Share ID</label>
        <input id="share-id" name="shareId" value="${escapeAttribute(shareId)}" pattern="[A-Za-z0-9._-]{1,128}" maxlength="128" required />
        <label for="reason">Reason</label>
        <select id="reason" name="reason" required>
          <option value="privacy">Privacy or consent</option><option value="harassment">Harassment or safety</option>
          <option value="copyright">Copyright</option><option value="unsafe">Illegal or unsafe content</option><option value="other">Other</option>
        </select>
        <label for="details">What should our review team know?</label>
        <textarea id="details" name="details" maxlength="1000" rows="6"></textarea>
        <label for="contact">Contact email (optional)</label>
        <input id="contact" name="contact" type="email" autocomplete="email" maxlength="254" />
        <button type="submit">Send report</button>
        <p id="report-status" class="form-status" role="status" aria-live="polite"></p>
      </form>
      <p>For a detailed copyright notice or urgent escalation, email <a href="mailto:abuse@goodeye.coach">abuse@goodeye.coach</a>.</p>`,
  );
}

function shareViewerPage(shareId: string): string {
  return `<section class="viewer-shell" data-share-id="${escapeAttribute(shareId)}">
    <div class="viewer-heading"><span class="eyebrow">Private Good Eye review</span><h1 id="viewer-title">Opening review…</h1></div>
    <div id="viewer-stage" class="viewer-stage" aria-live="polite">
      <div class="loading-orbit" aria-hidden="true"></div><p>Securely resolving this link…</p>
    </div>
    <div id="viewer-playback-controls" class="viewer-playback-controls" hidden>
      <button type="button" data-frame-step="-1" aria-label="Step back one frame">−1 frame</button>
      <div class="viewer-speed-controls" role="group" aria-label="Playback speed">
        <button type="button" data-playback-rate="0.25">¼×</button>
        <button type="button" data-playback-rate="0.5">½×</button>
        <button type="button" data-playback-rate="1" aria-pressed="true">1×</button>
      </div>
      <button type="button" data-frame-step="1" aria-label="Step forward one frame">+1 frame</button>
      <output id="viewer-time" aria-live="off">0:00.00 / 0:00.00</output>
    </div>
    <div id="viewer-meta" class="viewer-meta" hidden></div>
    <div class="viewer-actions">
      <a id="download-link" class="button" href="#" hidden>Download a copy</a>
      <a class="quiet-link" href="/abuse?share=${encodeURIComponent(shareId)}">Report this content</a>
    </div>
    <p class="viewer-note">Anyone with the complete private link can view this export. Downloads cannot be revoked.</p>
  </section>`;
}

async function initializeShareViewer(shareId: string): Promise<void> {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const secretFromLink = hash.get("t");
  const secret = secretFromLink ?? readSessionShareSecret(shareId);
  if (secretFromLink) rememberSessionShareSecret(shareId, secretFromLink);
  // Remove the bearer secret before any navigation, referrer, screenshot, or copied
  // address can expose it. Session storage keeps same-tab reloads working without
  // turning the bearer credential into durable browser storage.
  window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  if (!secret) {
    showViewerError("This private link is incomplete. Ask the sender to share it again.");
    return;
  }

  let refreshTimer = 0;
  let currentResolution: MediaResolution | null = null;
  const refresh = async (): Promise<void> => {
    try {
      const resolution = await postJson<MediaResolution>(resolveEndpoint, { shareId, secret });
      const previous = currentResolution;
      currentResolution = resolution;
      renderResolvedMedia(resolution, previous);
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(
        () => void refresh(),
        Math.max(30_000, resolution.mediaExpiresAt - Date.now() - 30_000),
      );
    } catch {
      if (currentResolution && currentResolution.mediaExpiresAt > Date.now()) {
        window.setTimeout(() => void refresh(), 15_000);
      } else {
        currentResolution = null;
        showViewerError("This review is unavailable. It may have expired or been revoked.");
      }
    }
  };
  await refresh();
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && currentResolution && currentResolution.mediaExpiresAt - Date.now() < 60_000) {
      void refresh();
    }
  });
}

function readSessionShareSecret(shareId: string): string | null {
  try {
    return window.sessionStorage.getItem(`good-eye-share:${shareId}`);
  } catch {
    return null;
  }
}

function rememberSessionShareSecret(shareId: string, secret: string): void {
  try {
    window.sessionStorage.setItem(`good-eye-share:${shareId}`, secret);
  } catch {
    // Private browsing or a restrictive storage policy can disable sessionStorage;
    // the current page still retains the credential in memory.
  }
}

function renderResolvedMedia(resolution: MediaResolution, previous: MediaResolution | null): void {
  const stage = requiredElement<HTMLDivElement>("#viewer-stage");
  const title = requiredElement<HTMLHeadingElement>("#viewer-title");
  const meta = requiredElement<HTMLDivElement>("#viewer-meta");
  const download = requiredElement<HTMLAnchorElement>("#download-link");
  title.textContent = resolution.title;
  meta.hidden = false;
  meta.textContent = shareAvailabilityCopy(resolution);
  download.hidden = false;
  download.href = resolution.downloadUrl;
  download.rel = "noopener noreferrer";

  if (resolution.mediaType === "video/mp4") {
    const existing = stage.querySelector("video");
    const priorTime = existing?.currentTime ?? 0;
    const wasPaused = existing?.paused ?? true;
    const priorPlaybackRate = existing?.playbackRate ?? 1;
    if (!existing || !previous) {
      stage.replaceChildren();
      const video = document.createElement("video");
      video.controls = true;
      video.playsInline = true;
      video.preload = "metadata";
      video.src = resolution.mediaUrl;
      video.addEventListener("error", () => showTransientMediaError());
      stage.append(video);
      initializePlaybackControls(video);
    } else if (previous.mediaUrl !== resolution.mediaUrl) {
      existing.addEventListener(
        "loadedmetadata",
        () => {
          existing.currentTime = Math.min(priorTime, existing.duration || priorTime);
          existing.playbackRate = priorPlaybackRate;
          if (!wasPaused) void existing.play().catch(() => undefined);
        },
        { once: true },
      );
      existing.src = resolution.mediaUrl;
      existing.load();
    }
  } else {
    hidePlaybackControls();
    let image = stage.querySelector("img");
    if (!image) {
      stage.replaceChildren();
      image = document.createElement("img");
      image.alt = resolution.title;
      stage.append(image);
    }
    image.src = resolution.mediaUrl;
  }
}

function showViewerError(message: string): void {
  const stage = requiredElement<HTMLDivElement>("#viewer-stage");
  stage.innerHTML = `<div class="error-mark" aria-hidden="true">!</div><h2>Review unavailable</h2><p>${escapeHtml(message)}</p>`;
  requiredElement<HTMLHeadingElement>("#viewer-title").textContent = "We couldn’t open this review";
  requiredElement<HTMLDivElement>("#viewer-meta").hidden = true;
  requiredElement<HTMLAnchorElement>("#download-link").hidden = true;
  hidePlaybackControls();
}

function initializePlaybackControls(video: HTMLVideoElement): void {
  const controls = requiredElement<HTMLDivElement>("#viewer-playback-controls");
  controlledVideo = video;
  controls.hidden = false;

  if (controls.dataset.initialized !== "true") {
    controls.dataset.initialized = "true";
    controls.querySelectorAll<HTMLButtonElement>("[data-frame-step]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!controlledVideo) return;
        controlledVideo.pause();
        const direction = Number(button.dataset.frameStep);
        const duration = Number.isFinite(controlledVideo.duration) ? controlledVideo.duration : Number.MAX_SAFE_INTEGER;
        controlledVideo.currentTime = Math.min(duration, Math.max(0, controlledVideo.currentTime + direction * FRAME_SECONDS));
        updatePlaybackReadout(controlledVideo);
      });
    });
    controls.querySelectorAll<HTMLButtonElement>("[data-playback-rate]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!controlledVideo) return;
        controlledVideo.playbackRate = Number(button.dataset.playbackRate);
        updatePlaybackReadout(controlledVideo);
      });
    });
  }

  for (const eventName of ["loadedmetadata", "durationchange", "timeupdate", "ratechange"] as const) {
    video.addEventListener(eventName, () => updatePlaybackReadout(video));
  }
  updatePlaybackReadout(video);
}

function hidePlaybackControls(): void {
  controlledVideo = null;
  const controls = document.querySelector<HTMLDivElement>("#viewer-playback-controls");
  if (controls) controls.hidden = true;
}

function updatePlaybackReadout(video: HTMLVideoElement): void {
  if (video !== controlledVideo) return;
  const controls = requiredElement<HTMLDivElement>("#viewer-playback-controls");
  controls.querySelectorAll<HTMLButtonElement>("[data-playback-rate]").forEach((button) => {
    button.setAttribute("aria-pressed", String(Number(button.dataset.playbackRate) === video.playbackRate));
  });
  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  requiredElement<HTMLOutputElement>("#viewer-time").textContent = `${formatMediaTime(video.currentTime)} / ${formatMediaTime(duration)}`;
}

function showTransientMediaError(): void {
  const note = document.querySelector(".viewer-note");
  if (note) note.textContent = "Playback was interrupted. Keep this page open while Good Eye refreshes secure access.";
}

function initializeDeletionForm(): void {
  const form = requiredElement<HTMLFormElement>("#deletion-form");
  const status = requiredElement<HTMLParagraphElement>("#deletion-status");
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const button = requiredElement<HTMLButtonElement>("button", form);
    const formData = new FormData(form);
    button.disabled = true;
    status.textContent = "Submitting…";
    try {
      await postJson(deletionEndpoint, { email: formData.get("email") });
      form.reset();
      status.textContent = "Request received. Our privacy team must verify your identity before deletion; email privacy@goodeye.coach if you do not hear back.";
    } catch {
      status.textContent = "We couldn’t submit the request. Try again later or email privacy@goodeye.coach.";
    } finally {
      button.disabled = false;
    }
  });
}

function initializeReportForm(): void {
  const form = requiredElement<HTMLFormElement>("#report-form");
  const status = requiredElement<HTMLParagraphElement>("#report-status");
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const button = requiredElement<HTMLButtonElement>("button", form);
    const formData = new FormData(form);
    button.disabled = true;
    status.textContent = "Sending…";
    try {
      await postJson(reportEndpoint, {
        shareId: formData.get("shareId"),
        reason: formData.get("reason"),
        details: formData.get("details"),
        contact: formData.get("contact"),
      });
      form.reset();
      status.textContent = "Report received. Thank you for bringing this to our attention.";
    } catch {
      status.textContent = "We couldn’t send the report. Try again or email abuse@goodeye.coach.";
    } finally {
      button.disabled = false;
    }
  });
}

async function postJson<T = unknown>(endpoint: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
    credentials: "omit",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return (await response.json()) as T;
}

function articlePage(title: string, intro: string, body: string): string {
  return `<article class="legal-page"><header><span class="eyebrow">Good Eye Coach</span><h1>${title}</h1><p class="lede">${intro}</p></header><div class="prose">${body}</div></article>`;
}

function notFoundPage(): string {
  return `<section class="empty-page"><span class="error-mark">404</span><h1>That page is out of frame.</h1><p>Check the address or return to Good Eye Coach.</p><a class="button" href="/">Return home</a></section>`;
}

function normalizedPath(path: string): string {
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path;
}

function headingFor(path: string): string {
  return ({
    "/brand": "Brand guidelines",
    "/privacy": "Privacy",
    "/terms": "Terms",
    "/support": "Support",
    "/deletion": "Cloud data deletion",
    "/abuse": "Report content",
  } as Record<string, string>)[path] ?? "Good Eye Coach";
}

function formatBytes(bytes: number): string {
  if (bytes < 1_000_000) return `${Math.max(1, Math.round(bytes / 1000))} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

function formatDate(epochMs: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(epochMs);
}

function formatMediaTime(seconds: number): string {
  const safeSeconds = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const minutes = Math.floor(safeSeconds / 60);
  return `${minutes}:${(safeSeconds % 60).toFixed(2).padStart(5, "0")}`;
}

function shareAvailabilityCopy(resolution: MediaResolution): string {
  const size = formatBytes(resolution.byteLength);
  if (resolution.expiryPolicy === "subscription" || resolution.shareExpiresAt === null) {
    return `${size} · Available while the sender’s Good Eye+ subscription is active`;
  }
  return `${size} · Link expires ${formatDate(resolution.shareExpiresAt)}`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character] ?? character);
}

function escapeAttribute(value: string): string {
  return escapeHtml(value);
}

function requiredElement<T extends Element>(selector: string, root: ParentNode = document): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}
